// A day's trips, told as cards: what you were on while driving (the clock's project, the tasks whose timer ran
// during the trip) and what the trip cost (fuel by km, time by rate). Pure — the trips come from segmentDay.
//
// trip (segmentDay): { start, end, distanceM, drive, points:[{lat,lng}] }
// points: the day's track points { t, lat, lng, speed, clocked, projectId }

import { distM } from './segment.js';

// Starting values until the vehicle is set (meta/settings.trip) — shown as defaults, never as facts.
export const TRIP_DEFAULTS = { lPer100km: 13, fuelPrice: 1.8, hourlyRate: 0 };

const hmToMs = (date, hm) => {
  const [h, m] = String(hm || '').split(':').map(Number);
  if (!date || !Number.isFinite(h)) return null;
  return new Date(`${date}T${String(h).padStart(2, '0')}:${String(Number.isFinite(m) ? m : 0).padStart(2, '0')}:00`).getTime();
};
const overlap = (a0, a1, b0, b1) => Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));

/**
 * @returns trips + { n, durationMs, km, projectId, onClock, tasks:[{ taskId, title, projectId, ms }] } — the project is
 *   the work session covering most of the trip, else the one most of its points were tagged with.
 */
export function describeTrips({ trips = [], points = [], sessions = [], tasks = [], date = null }) {
  return trips.map((tr, i) => {
    let best = null; let bestMs = 0;
    for (const s of sessions) {
      if (s.type === 'travel' || !s.startedAt) continue;
      const ms = overlap(tr.start, tr.end, s.startedAt, s.endedAt || tr.end);
      if (ms > bestMs) { bestMs = ms; best = s; }
    }
    const inTrip = points.filter((p) => p && p.t >= tr.start && p.t <= tr.end);
    let projectId = best?.projectId || null;
    if (!projectId) {
      const votes = new Map();
      inTrip.forEach((p) => { if (p.projectId) votes.set(p.projectId, (votes.get(p.projectId) || 0) + 1); });
      projectId = [...votes].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    }
    const byTask = new Map();
    for (const t of tasks) {
      for (const e of t.timeEntries || []) {
        const d = e.date || date;
        const a = hmToMs(d, e.startTime); const b = hmToMs(d, e.endTime);
        if (a == null || b == null || b <= a) continue;
        const ms = overlap(tr.start, tr.end, a, b);
        if (ms < 60000) continue;
        const row = byTask.get(t.id) || { taskId: t.id, title: t.title || 'Task', projectId: t.projectId || null, ms: 0 };
        row.ms += ms;
        byTask.set(t.id, row);
      }
    }
    return {
      ...tr, n: i + 1,
      durationMs: tr.end - tr.start,
      km: Math.round((tr.distanceM || 0) / 100) / 10,
      projectId,
      onClock: !!best || inTrip.some((p) => p.clocked),
      tasks: [...byTask.values()].sort((a, b) => b.ms - a.ms),
    };
  });
}

/** The project whose geofence a spot falls in (its radius + 60 m of GPS slack), else null. */
export const projectAt = (projects, p) => (projects || []).find((x) => x?.geofence && distM({ lat: x.geofence.lat, lng: x.geofence.lng }, p) <= ((x.geofence.radius || 90) + 60)) || null;

/** Fuel (km × L/100 km × $/L) and time (hours × $/h) for one trip. Money to the cent. */
export function tripCost(trip, { lPer100km = 0, fuelPrice = 0, hourlyRate = 0 } = {}) {
  const km = (trip.distanceM || 0) / 1000;
  const hours = Math.max(0, (trip.end - trip.start) / 3600000);
  const litres = (km * (lPer100km || 0)) / 100;
  const cents = (x) => Math.round(x * 100) / 100;
  const fuel = cents(litres * (fuelPrice || 0));
  const time = cents(hours * (hourlyRate || 0));
  return { km: Math.round(km * 10) / 10, hours, litres: Math.round(litres * 10) / 10, fuel, time, total: cents(fuel + time) };
}

/**
 * Legs (segmentDay trips) → journeys. A journey runs from leaving a place until you're back within `radiusM` of it
 * (a ROUND TRIP, its legs inside, parked stretches between them); one that never comes back — or is broken by a
 * `maxGapMs` pause — is ONE WAY. Legs shorter than `minLegM` (moving the truck across the yard) are dropped.
 * A journey has the leg shape (start, end, distanceM, drive, points) so describeTrips / tripCost take it as is.
 */
export function groupJourneys(legs = [], { radiusM = 300, maxGapMs = 3 * 3600000, minLegM = 150 } = {}) {
  const real = legs.filter((l) => (l.distanceM || 0) >= minLegM && l.points?.length > 1).sort((a, b) => a.start - b.start);
  const out = [];
  let cur = null;
  const close = (roundTrip) => {
    if (!cur) return;
    const ls = cur.legs;
    const stops = ls.slice(1).map((l, i) => {
      const at = ls[i].points[ls[i].points.length - 1];
      return { lat: at.lat, lng: at.lng, start: ls[i].end, end: l.start, durationMs: l.start - ls[i].end };
    });
    out.push({
      start: ls[0].start, end: ls[ls.length - 1].end,
      distanceM: ls.reduce((a, l) => a + (l.distanceM || 0), 0),
      drive: ls.some((l) => l.drive),
      points: ls.flatMap((l) => l.points),
      roundTrip, legs: ls, stops,
      drivingMs: ls.reduce((a, l) => a + (l.end - l.start), 0),
    });
    cur = null;
  };
  for (const leg of real) {
    if (cur && leg.start - cur.legs[cur.legs.length - 1].end > maxGapMs) close(false);
    if (!cur) cur = { home: leg.points[0], legs: [] };
    cur.legs.push(leg);
    if (distM(cur.home, leg.points[leg.points.length - 1]) <= radiusM) close(true);
  }
  close(false);
  return out;
}
